import { useState, useEffect } from 'react';
import { dataPrincipalsService, DataPrincipalStats } from '../../../services/dataPrincipals-service';
import { requestsService, RightsRequestsSummary } from '../../../services/requests-service';
import { auditLogsService } from '../../../services/auditLogsService';
import { noticesService } from '../../../services/notices-service';

export interface DashboardData {
  dataPrincipals: DataPrincipalStats | null;
  requests: RightsRequestsSummary | null;
  auditStats: any | null;
  notices: any | null;
}

export interface DashboardLoadingState {
  dataPrincipals: boolean;
  requests: boolean;
  auditStats: boolean;
  notices: boolean;
}

export interface DashboardErrorState {
  dataPrincipals: string | null;
  requests: string | null;
  auditStats: string | null;
  notices: string | null;
}

export const useDashboardData = (startDate?: string, endDate?: string) => {
  const [data, setData] = useState<DashboardData>({
    dataPrincipals: null,
    requests: null,
    auditStats: null,
    notices: null,
  });

  const [loading, setLoading] = useState<DashboardLoadingState>({
    dataPrincipals: true,
    requests: true,
    auditStats: true,
    notices: true,
  });

  const [errors, setErrors] = useState<DashboardErrorState>({
    dataPrincipals: null,
    requests: null,
    auditStats: null,
    notices: null,
  });

  useEffect(() => {
    // Fetch Data Principals Stats
    const fetchDataPrincipals = async () => {
      setLoading((prev) => ({ ...prev, dataPrincipals: true }));
      setErrors((prev) => ({ ...prev, dataPrincipals: null }));
      try {
        const stats = await dataPrincipalsService.getDataPrincipalStats();
        setData((prev) => ({ ...prev, dataPrincipals: stats }));
      } catch (error: any) {
        console.error('Error fetching data principals stats:', error);
        setErrors((prev) => ({ ...prev, dataPrincipals: error.message || 'Failed to fetch data' }));
      } finally {
        setLoading((prev) => ({ ...prev, dataPrincipals: false }));
      }
    };

    // Fetch Requests Summary
    const fetchRequests = async () => {
      setLoading((prev) => ({ ...prev, requests: true }));
      setErrors((prev) => ({ ...prev, requests: null }));
      try {
        const response = await requestsService.getRequestsSummary();
        setData((prev) => ({ ...prev, requests: response.data }));
      } catch (error: any) {
        console.error('Error fetching requests summary:', error);
        setErrors((prev) => ({ ...prev, requests: error.message || 'Failed to fetch data' }));
      } finally {
        setLoading((prev) => ({ ...prev, requests: false }));
      }
    };

    // Fetch Audit Statistics
    const fetchAuditStats = async () => {
      setLoading((prev) => ({ ...prev, auditStats: true }));
      setErrors((prev) => ({ ...prev, auditStats: null }));
      try {
        const stats = await auditLogsService.getAuditStatistics(startDate, endDate);
        setData((prev) => ({ ...prev, auditStats: stats }));
      } catch (error: any) {
        console.error('Error fetching audit statistics:', error);
        setErrors((prev) => ({ ...prev, auditStats: error.message || 'Failed to fetch data' }));
      } finally {
        setLoading((prev) => ({ ...prev, auditStats: false }));
      }
    };

    // Fetch Notices (placeholder - may need summary endpoint)
    const fetchNotices = async () => {
      setLoading((prev) => ({ ...prev, notices: true }));
      setErrors((prev) => ({ ...prev, notices: null }));
      try {
        // TODO: Implement notices summary endpoint or use list with filters
        // For now, we'll set to null
        setData((prev) => ({ ...prev, notices: null }));
      } catch (error: any) {
        console.error('Error fetching notices:', error);
        setErrors((prev) => ({ ...prev, notices: error.message || 'Failed to fetch data' }));
      } finally {
        setLoading((prev) => ({ ...prev, notices: false }));
      }
    };

    // Fetch all data in parallel
    fetchDataPrincipals();
    fetchRequests();
    fetchAuditStats();
    fetchNotices();
  }, [startDate, endDate]);

  return { data, loading, errors };
};

